import React, { useEffect, useId, useRef, useState } from "react";
import styles from "./LogInComponent.module.scss";
import { CircularProgress } from "@mui/material";
var classNames = require("classnames");

// The backend keeps a login challenge alive for 5 minutes (see docs/2fa-api.md).
const CHALLENGE_LIFETIME_SECONDS = 5 * 60;

interface TwoFactorFormProps {
  /** Opaque challenge id returned by step 1. Held in memory only — never persisted. */
  challengeId: string;
  isLoading: boolean;
  errorMessage: string;
  onVerify: (code: string) => void;
  onCancel: () => void;
  /** Called when the 5 minute challenge window runs out. */
  onExpire: () => void;
}

const formatCountdown = (totalSeconds: number) => {
  var minutes = Math.floor(totalSeconds / 60);
  var seconds = totalSeconds % 60;
  return minutes + ":" + (seconds < 10 ? "0" + seconds : seconds);
};

const TwoFactorForm = ({
  challengeId,
  isLoading,
  errorMessage,
  onVerify,
  onCancel,
  onExpire,
}: TwoFactorFormProps) => {
  const [code, setCode] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(CHALLENGE_LIFETIME_SECONDS);
  const inputRef = useRef<HTMLInputElement>(null);
  // The desktop and mobile layouts both mount this form, so the input id has
  // to be unique per instance for the label to bind correctly.
  const inputId = useId();
  // The parent re-creates onExpire each render; a ref keeps it out of the
  // countdown effect's deps so the timer never restarts mid-countdown.
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Restart the countdown whenever a new challenge is issued.
  useEffect(() => {
    setSecondsLeft(CHALLENGE_LIFETIME_SECONDS);
    setCode("");
  }, [challengeId]);

  useEffect(() => {
    if (secondsLeft <= 0) {
      onExpireRef.current();
      return;
    }
    const timer = setTimeout(() => setSecondsLeft((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const codeChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    // TOTP codes are 6 digits — drop anything else so a pasted code still works.
    setCode(event.target.value.replace(/\D/g, "").slice(0, 6));
  };

  const submitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    if (code.length != 6 || isLoading) return;
    onVerify(code);
  };

  return (
    <form onSubmit={submitHandler} className="w-full md:w-[440px] px-5">
      <div
        className={classNames(
          "flex justify-start text-left flex-col w-full ",
          styles.signinform
        )}
      >
        <h3 className="text-darkblue text-20 sm:text-30 font-semibold mb-[12px] md:text-left text-center">
          Two-Factor Authentication
        </h3>
        <p className="text-graydark text-15 font-primary mb-[29px] md:text-left text-center">
          Enter the 6-digit code from your authenticator app.
        </p>
        <div
          className={classNames(
            styles.inputContainer,
            "md:mb-[29px] mb-4 last:mb-0"
          )}
        >
          <label
            htmlFor={inputId}
            className="text-black2 text-15 font-medium mb-[12px] block"
          >
            Verification Code
          </label>
          <input
            id={inputId}
            ref={inputRef}
            type="text"
            name="code"
            value={code}
            onChange={codeChangeHandler}
            required
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            className="htzxs:h-[70px] htzxs:py-0 md:text-sm text-xs py-3 tracking-[0.5em] placeholder:tracking-normal placeholder:text-vorpgraylight placeholder:text-xs md:placeholder:text-14 text-black2 rounded-[9px] px-[24px] w-full focus:outline-none"
            placeholder="123456"
            style={{ border: errorMessage ? "1px solid red" : "" }}
          />
        </div>
        <div className="text-graydark text-xs font-primary">
          This code expires in {formatCountdown(secondsLeft)}
        </div>
        <div
          className={classNames(
            styles.buttonContainer,
            "htzxs:mt-[37px] mt-5"
          )}
        >
          <div className="relative w-full">
            <button
              type="submit"
              disabled={code.length != 6 || isLoading}
              className={classNames(
                isLoading || code.length != 6
                  ? "opacity-50 pointer-events-none"
                  : "",
                "transition-all hover:bg-white hover:text-vorpblue border border-vorpblue bg-vorpblue text-15 text-white w-full flex items-center justify-center rounded-[6px] py-[0.8rem]"
              )}
            >
              Verify
            </button>
            <div
              className={classNames(
                isLoading ? "block" : "hidden",
                " absolute top-[55%] left-1/2 -translate-x-1/2 -translate-y-1/2"
              )}
            >
              <CircularProgress />
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-15 text-vorpblue w-full text-center mt-4"
          >
            Back to Sign In
          </button>
        </div>
      </div>
      <div className="text-red-500 text-15 font-primary mt-3">
        {errorMessage}
      </div>
    </form>
  );
};

export default TwoFactorForm;
